/**
 * Serviço de Configurações
 *
 * Funções para comunicação com a API de configurações.
 * Gerencia perfil do usuário, configurações do tenant
 * e administração de usuários.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { api } from './api';
import type { ApiResponse, ApiMeta, PaginationParams } from '@/types/common';
import type { UserProfile, TenantSettings, UserListItem } from '@/types/settings';

// ============================================================================
// PERFIL DO USUÁRIO
// ============================================================================

/**
 * Busca o perfil do usuário logado
 *
 * @returns Perfil do usuário
 */
export async function getProfile(): Promise<UserProfile> {
  const response = await api.get<ApiResponse<UserProfile>>('/users/me');

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar perfil');
  }

  return response.data.data;
}

/**
 * Atualiza o perfil do usuário logado
 *
 * @param data - Dados a serem atualizados
 * @returns Perfil atualizado
 */
export async function updateProfile(
  data: Partial<Pick<UserProfile, 'name' | 'email' | 'phone'>>,
): Promise<UserProfile> {
  const response = await api.put<ApiResponse<UserProfile>>('/users/me', data);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao atualizar perfil');
  }

  return response.data.data;
}

/**
 * Altera a senha do usuário logado
 *
 * @param data - Senhas atual e nova
 */
export async function changePassword(data: {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<void> {
  const response = await api.post<ApiResponse<void>>('/users/me/password', data);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao alterar senha');
  }
}

// ============================================================================
// CONFIGURAÇÕES DO TENANT
// ============================================================================

/**
 * Busca as configurações do tenant
 *
 * @returns Configurações do tenant
 */
export async function getTenantSettings(): Promise<TenantSettings> {
  const response = await api.get<ApiResponse<TenantSettings>>('/tenants/me');

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao buscar configurações');
  }

  return response.data.data;
}

/**
 * Atualiza as configurações do tenant
 *
 * @param data - Dados a serem atualizados
 * @returns Configurações atualizadas
 */
export async function updateTenantSettings(
  data: Partial<TenantSettings>,
): Promise<TenantSettings> {
  const response = await api.put<ApiResponse<TenantSettings>>('/tenants/me', data);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao atualizar configurações');
  }

  return response.data.data;
}

// ============================================================================
// GERENCIAMENTO DE USUÁRIOS
// ============================================================================

/**
 * Lista os usuários do tenant
 *
 * @param params - Parâmetros de paginação
 * @returns Lista de usuários e metadados
 */
export async function listUsers(
  params: PaginationParams & { search?: string } = {},
): Promise<{ data: UserListItem[]; meta: ApiMeta }> {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  const response = await api.get<ApiResponse<UserListItem[]>>(
    `/users?${queryParams.toString()}`,
  );

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao listar usuários');
  }

  const pagination = (response.data as unknown as { pagination?: ApiMeta }).pagination;
  return {
    data: response.data.data,
    meta: pagination || response.data.meta || { page: 1, perPage: 20, total: 0, totalPages: 1 },
  };
}

/**
 * Cria um novo usuário
 *
 * @param data - Dados do novo usuário
 * @returns Usuário criado
 */
export async function createUser(data: {
  name: string;
  email: string;
  password: string;
  role: string;
  phone?: string;
}): Promise<UserListItem> {
  const response = await api.post<ApiResponse<UserListItem>>('/users', data);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao criar usuário');
  }

  return response.data.data;
}

/**
 * Atualiza um usuário existente
 *
 * @param id - ID do usuário
 * @param data - Dados a serem atualizados
 * @returns Usuário atualizado
 */
export async function updateUser(
  id: string,
  data: Partial<{
    name: string;
    email: string;
    role: string;
    phone: string;
    isActive: boolean;
  }>,
): Promise<UserListItem> {
  const response = await api.put<ApiResponse<UserListItem>>(`/users/${id}`, data);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao atualizar usuário');
  }

  return response.data.data;
}

/**
 * Remove um usuário (soft delete)
 *
 * @param id - ID do usuário
 */
export async function deleteUser(id: string): Promise<void> {
  const response = await api.delete<ApiResponse<void>>(`/users/${id}`);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao remover usuário');
  }
}

export default {
  getProfile,
  updateProfile,
  changePassword,
  getTenantSettings,
  updateTenantSettings,
  listUsers,
  createUser,
  updateUser,
  deleteUser,
};
